// Chakra imports
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Divider,
  Flex,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';

import { cancelCharge, generatePaymentsForCharge, getCharge } from 'services/api.service';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { maskCurrency } from 'utils/number';
import { ChargeStatus, StatusRender } from '.';
import { ChargeType, ItemChargeStatus } from 'types/Charge';
import { CloseIcon, RepeatIcon } from '@chakra-ui/icons';
import { FC, useState } from 'react';
import { BsBoxArrowUpRight } from 'react-icons/bs';
import { MdOutlineLink } from 'react-icons/md';
import CopyableText from 'components/CopyableText';
import { Field, Section } from 'components/DetailSection';
import Devedor from './Devedor';
import ListItens from './ChargeItens';
import ChargePayments from './ChargePayments';
import Webhooks from './Webhooks';
import { isExpired, toAmount } from './chargeSummary';

const formatDate = (value?: string | null) =>
  value ? DateTime.fromISO(value).toFormat('dd/MM/yyyy HH:mm:ss') : '---';

const CancelCharge: FC<{ id: string; hasOpenBillet: boolean; refetch: () => void }> = ({
  id,
  hasOpenBillet,
  refetch,
}) => {
  const toast = useToast();
  const [canceling, setCanceling] = useState(false);

  const handleCancel = () => {
    setCanceling(true);
    cancelCharge(id)
      .then(() => {
        toast({
          title: 'Sucesso',
          description: 'Cobrança cancelada com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        refetch();
      })
      .catch((err) => {
        // O servidor explica a recusa (boleto protestado, banco recusou a baixa): ela
        // diz ao lojista o que fazer, então sobe inteira.
        toast({
          title: 'Erro',
          description: err.response?.data?.message || 'Erro ao cancelar cobrança',
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      })
      .finally(() => setCanceling(false));
  };

  return (
    <Popover>
      <PopoverTrigger>
        <Button size="sm" colorScheme="red" rightIcon={<CloseIcon />} isLoading={canceling}>
          Cancelar
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader>Confirmação!</PopoverHeader>
        <PopoverBody>
          <Text>
            Deseja realmente cancelar a cobrança?
            {hasOpenBillet && ' O boleto será baixado no banco e deixará de poder ser pago.'}
          </Text>
          <Button size="sm" colorScheme="red" mt={3} isLoading={canceling} onClick={handleCancel}>
            Cancelar cobrança
          </Button>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default function Charge() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const muted = useColorModeValue('gray.500', 'whiteAlpha.600');
  const panelBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  const { data, refetch, isFetching, isLoading, error } = useQuery(['getCharge', id], () => getCharge(id));

  const handleGenerate = (type: 'pix' | 'billet') => {
    if (!data) return;

    generatePaymentsForCharge(data.id, type)
      .then(() => {
        toast({
          title: 'Sucesso',
          description: `${type === 'pix' ? 'Pix' : 'Boleto'} gerado com sucesso`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        refetch();
      })
      .catch((err) => {
        toast({
          title: 'Erro',
          description: err.response?.data?.message || 'Erro ao gerar cobrança',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      });
  };

  // O `if (!data) return <div>Carregando...</div>` que existia aqui curto-circuitava tudo:
  // um erro na consulta deixava a tela presa em "Carregando..." para sempre.
  if (error)
    return (
      <Box>
        <Card px={3} py={5}>
          <Center>
            <Alert
              status="error"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="200px"
              borderRadius="md"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                Erro ao carregar dados
              </AlertTitle>
              <AlertDescription maxWidth="sm">
                Não foi possível carregar os detalhes da cobrança. Tente novamente mais tarde.
              </AlertDescription>
              <Button mt={4} colorScheme="red" onClick={() => refetch()}>
                Tentar novamente
              </Button>
            </Alert>
          </Center>
        </Card>
      </Box>
    );

  if (isLoading || !data)
    return (
      <Box>
        <Card px={{ base: 3, md: 5 }} py={4}>
          <Skeleton height="16px" width="160px" mb={3} />
          <Skeleton height="36px" width="220px" mb={2} />
          <Skeleton height="16px" width="280px" mb={6} />
          <Skeleton height="180px" mb={6} />
          <SimpleGrid columns={{ base: 1, md: 3, xl: 4 }} spacing={4}>
            {Array(8)
              .fill(0)
              .map((_, i) => (
                <Box key={i}>
                  <Skeleton height="14px" width="90px" mb={2} />
                  <Skeleton height="20px" width="140px" />
                </Box>
              ))}
          </SimpleGrid>
        </Card>
      </Box>
    );

  const expired = isExpired(data.expireAt, data.status);
  const refunded = toAmount(data.amountRefunded);

  const details = [
    { title: 'Tipo', content: ChargeType[data.chargeType] },
    { title: 'Descrição', content: data.description || '---' },
    { title: 'Nome do cliente', content: data.CustomerClient?.name || '---' },
    { title: 'Criação', content: formatDate(data.createdAt) },
    { title: 'Pagamento', content: formatDate(data.payedAt) },
    { title: 'Expiração', content: formatDate(data.expireAt) },
    ...(data.isDeposit ? [{ title: 'Depósito', content: <Badge colorScheme="blue">Sim</Badge> }] : []),
  ];

  return (
    <Box>
      <SimpleGrid mb="20px" columns={{ md: 1 }} spacing={{ base: '20px', xl: '20px' }}>
        <Card px={{ base: 3, md: 5 }} py={4}>
          <Flex direction={{ base: 'column', lg: 'row' }} justify="space-between" gap={5}>
            <Box minW={0}>
              <Text fontSize="sm" color={muted}>
                Detalhes da cobrança
              </Text>
              <HStack spacing={3} align="center" mt={1} flexWrap="wrap">
                <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" lineHeight="1.1">
                  {maskCurrency(data.amount)}
                </Text>
                <StatusRender status={data.status} />
                {expired && <Badge colorScheme="red">Expirada</Badge>}
              </HStack>
              <Text mt={2} color={muted}>
                {ChargeType[data.chargeType]}
                {data.createdAt && ` · criada em ${formatDate(data.createdAt)}`}
              </Text>
              <Text color={muted}>
                {data.payedAt
                  ? `Paga em ${formatDate(data.payedAt)}`
                  : `${expired ? 'Expirou' : 'Expira'} em ${formatDate(data.expireAt)}`}
              </Text>
              <Text fontWeight="semibold" mt={1}>
                {data.CustomerClient?.name}
              </Text>
            </Box>

            {/* O painel só existe quando tem conta a mostrar ou link a oferecer: repetir o
                valor que já está grande logo ao lado não é informação. */}
            {(refunded > 0 || data.urlPayment) && (
              <Stack bg={panelBg} borderRadius="lg" p={4} spacing={3} minW={{ lg: '320px' }}>
                {refunded > 0 && (
                  <>
                    <Flex justify="space-between" align="baseline" gap={4}>
                      <Text fontSize="sm" color={muted}>
                        Valor da cobrança
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
                        {maskCurrency(data.amount)}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" align="baseline" gap={4}>
                      <Text fontSize="sm" color={muted}>
                        Devolvido
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
                        {maskCurrency(-refunded)}
                      </Text>
                    </Flex>
                    <Divider />
                    <Flex justify="space-between" align="baseline" gap={4}>
                      <Text fontSize="sm" fontWeight="semibold">
                        Valor retido
                      </Text>
                      <Text fontSize="lg" fontWeight="bold" whiteSpace="nowrap">
                        {maskCurrency(toAmount(data.amount) - refunded)}
                      </Text>
                    </Flex>
                  </>
                )}

                {data.urlPayment && (
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="brand"
                      leftIcon={<BsBoxArrowUpRight />}
                      onClick={() => window.open(data.urlPayment || '', '_blank')}
                    >
                      Abrir
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="brand"
                      leftIcon={<MdOutlineLink />}
                      onClick={() => {
                        navigator.clipboard.writeText(data.urlPayment || '');
                        toast({ title: 'Link copiado', status: 'success', duration: 3000, isClosable: true });
                      }}
                    >
                      Copiar link
                    </Button>
                  </HStack>
                )}
              </Stack>
            )}
          </Flex>

          <HStack pt={3} spacing={2} flexWrap="wrap">
            <Button
              size="sm"
              colorScheme="brand"
              variant="outline"
              rightIcon={<RepeatIcon />}
              isLoading={isFetching}
              onClick={() =>
                refetch().then(() => {
                  toast({
                    title: 'Sucesso',
                    description: 'Cobrança atualizada com sucesso',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                  });
                })
              }
            >
              Atualizar
            </Button>
            {data.status === ChargeStatus.PENDING && (
              <CancelCharge
                id={data.id}
                hasOpenBillet={!!data.Billet?.some((billet) => billet.status === ItemChargeStatus.PENDING)}
                refetch={refetch}
              />
            )}
          </HStack>

          <Divider mt={3} />

          <Section title="Meios de pagamento">
            <ChargePayments data={data} onGenerate={handleGenerate} />
          </Section>

          {!!data.itens?.length && (
            <>
              <Divider />
              <Section title="Itens">
                <ListItens itens={data.itens} />
              </Section>
            </>
          )}

          {data.person && (
            <>
              <Divider />
              <Section title="Dados do pagador">
                <Devedor person={data.person} shipping={data.shipping} />
              </Section>
            </>
          )}

          <Divider />

          <Section title="Detalhes">
            <SimpleGrid columns={{ base: 1, md: 3, xl: 4 }} spacing={4}>
              {details.map((item) => (
                <Field key={item.title} title={item.title}>
                  {item.content}
                </Field>
              ))}
            </SimpleGrid>
          </Section>

          <Divider />

          <Section title="Identificadores">
            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
              <Field title="Cobrança">
                <CopyableText value={data.id} label="id da cobrança" />
              </Field>
              <Field title="Sequência">{data.sequence}</Field>
              <Field title="Id de referência">
                <CopyableText value={data.referenceId} label="id de referência" />
              </Field>
            </SimpleGrid>
          </Section>

          <Webhooks webhooks={data?.webhook || []} />
        </Card>
      </SimpleGrid>
    </Box>
  );
}
